'use client';

import { previewResetDemoInboxRoute, resetDemoInboxRoute } from '@/app/api/email/reset-demo/route.def';
import { getFtwAuthStatusRoute, saveFtwAuthRoute } from '@/app/api/ftw/auth/route.def';
import { listSponsorsRoute } from '@/app/api/sponsors/list/route.def';
import { ActivityLogButton } from '@/components/ActivityLogButton';
import { Button, buttonClassName } from '@/components/common/Button';
import { callApi } from '@/domains/common/api-client';
import { useFeatureFlag } from '@/domains/common/feature-flags';
import { showToast } from '@/domains/common/toast';
import { usePreference } from '@/domains/common/user-preferences';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { FormEvent, FunctionComponent, useEffect, useRef, useState } from 'react';

type AuthState = 'unknown' | 'authenticated' | 'unauthenticated' | 'logging_in';

function useSponsorIdFromPath(): string | null {
  const path = usePathname();
  if (!path) return null;
  const m = path.match(/^\/sponsor\/([^/]+)/);
  return m ? m[1] : null;
}

export const AdminLauncher: FunctionComponent = () => {
  const kpcOn = useFeatureFlag('kpc_cobranding');
  const sponsorId = useSponsorIdFromPath();
  const [open, setOpen] = useState(false);
  const [autoConfirm, setAutoConfirm] = usePreference('auto_confirm');
  const [showLogs, setShowLogs] = usePreference('show_tool_logs');
  const [authState, setAuthState] = useState<AuthState>('unknown');
  const [showCredForm, setShowCredForm] = useState(false);
  const [companyCode, setCompanyCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sponsorId) {
      setAuthState('unknown');
      return;
    }
    let cancelled = false;
    setAuthState('unknown');
    callApi(getFtwAuthStatusRoute, {
      gracefulFallback: () => {
        if (!cancelled) setAuthState('unauthenticated');
      },
    }).then(j => {
      if (cancelled) return;
      setAuthState(j.authenticated ? 'authenticated' : 'unauthenticated');
    });
    return () => {
      cancelled = true;
    };
  }, [sponsorId]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (!rootRef.current) return;
      if (e.target instanceof Node && rootRef.current.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggleAutoConfirm(): void {
    setAutoConfirm(!autoConfirm);
  }

  function toggleShowLogs(): void {
    setShowLogs(!showLogs);
  }

  async function submitCredentials(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!sponsorId) return;
    setAuthState('logging_in');
    await callApi(saveFtwAuthRoute, {
      body: { company_code: companyCode, username, password },
      gracefulFallback: () => {
        setPassword('');
        setAuthState('unauthenticated');
        showToast('An error occurred', { level: 'error' });
      },
    });
    setPassword('');
    setAuthState('authenticated');
    setShowCredForm(false);
    setCompanyCode('');
    setUsername('');
    showToast('FTW credentials saved', { level: 'success' });
  }

  const signingIn = authState === 'logging_in';
  const ftwLabel =
    authState === 'authenticated'
      ? 'Connected'
      : authState === 'logging_in'
        ? 'Saving…'
        : authState === 'unknown'
          ? '…'
          : 'Not connected';

  const statusText =
    authState === 'authenticated' ? 'text-green' : authState === 'unknown' ? 'text-ink-4' : 'text-ink-3';
  const statusDot =
    authState === 'authenticated' ? 'bg-green shadow-[0_0_0_3px_var(--color-green-soft)]' : 'bg-ink-4';

  // Hidden in KPlanConnect demo mode — the partner view shouldn't expose the
  // staff/dev console. Flip the kpc_cobranding flag off in the back office to
  // get it back.
  if (kpcOn) return null;

  return (
    <div className="fixed bottom-4 left-4 z-[50] flex flex-col items-start gap-2" ref={rootRef}>
      {open ? (
        <div
          className="flex max-h-[calc(100vh-96px)] w-80 animate-[admin-popover-in_0.14s_ease-out] flex-col overflow-y-auto rounded-lg border border-line-2 bg-bg-2 shadow-[0_1px_0_rgba(15,17,21,0.04),0_18px_48px_-12px_rgba(15,17,21,0.18)]"
          role="dialog"
          aria-label="Admin controls"
        >
          <div className="flex items-center justify-between border-b border-line px-3.5 pb-2.5 pt-3">
            <span className="text-[13px] font-semibold tracking-[-0.01em] text-ink">Admin</span>
            <button
              type="button"
              className="cursor-pointer border-none bg-transparent px-1 text-lg leading-none text-ink-4 transition-colors duration-150 hover:text-ink"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className={SECTION_CLASS}>
            <div className={SECTION_LABEL_CLASS}>Connections</div>
            <button
              type="button"
              className={ROW_CLASS}
              onClick={() => sponsorId && setShowCredForm(s => !s)}
              disabled={!sponsorId || signingIn}
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[12.5px] font-medium tracking-[-0.005em] text-ink">FTWilliam</span>
                <span className="text-[11.5px] text-ink-3">
                  {sponsorId
                    ? authState === 'authenticated'
                      ? 'Saved credentials — click to re-enter.'
                      : 'Add credentials so the agent can sign in.'
                    : 'Open a sponsor to manage credentials.'}
                </span>
              </span>
              <span
                className={`inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] font-medium tabular-nums ${statusText}`}
              >
                <span
                  className={`inline-block h-[7px] w-[7px] rounded-full ${statusDot}`}
                  aria-hidden="true"
                />
                {ftwLabel}
              </span>
            </button>
            {showCredForm && sponsorId ? (
              <form
                className="mt-1 flex flex-col gap-1.5 border-t border-dashed border-line px-2 pb-1 pt-2 [&_input:focus]:border-acc [&_input:focus]:shadow-[0_0_0_3px_var(--color-acc-soft)] [&_input:focus]:outline-none [&_input]:rounded-sm [&_input]:border [&_input]:border-line-2 [&_input]:bg-bg-2 [&_input]:px-[9px] [&_input]:py-[7px] [&_input]:font-sans [&_input]:text-[12.5px] [&_input]:text-ink [&_input]:transition-colors [&_input]:duration-150"
                onSubmit={submitCredentials}
              >
                <input
                  type="text"
                  placeholder="Company code"
                  autoComplete="organization"
                  value={companyCode}
                  onChange={e => setCompanyCode(e.target.value)}
                  disabled={signingIn}
                  required
                />
                <input
                  type="text"
                  placeholder="Username"
                  autoComplete="username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  disabled={signingIn}
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={signingIn}
                  required
                />
                <div className="mt-0.5 flex justify-end gap-1.5">
                  <Button
                    type="button"
                    size="small"
                    onClick={() => {
                      setShowCredForm(false);
                      setCompanyCode('');
                      setUsername('');
                      setPassword('');
                    }}
                    disabled={signingIn}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" size="small" variant="primary" disabled={signingIn}>
                    {signingIn ? 'Saving…' : 'Save'}
                  </Button>
                </div>
                <div className="px-0.5 text-[11px] text-ink-4">
                  Encrypted at rest. The agent uses these on the next pipeline run.
                </div>
              </form>
            ) : null}
          </div>

          <div className={SECTION_CLASS}>
            <div className={SECTION_LABEL_CLASS}>Preferences</div>
            <button
              type="button"
              className={ROW_CLASS}
              onClick={toggleAutoConfirm}
              aria-pressed={autoConfirm}
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[12.5px] font-medium tracking-[-0.005em] text-ink">
                  Skip permissions
                </span>
                <span className="text-[11.5px] text-ink-3">
                  {autoConfirm
                    ? 'Matilda runs high-risk tools without confirm chips.'
                    : 'Confirm before high-risk tool calls.'}
                </span>
              </span>
              <Switch on={autoConfirm} />
            </button>
            <button type="button" className={ROW_CLASS} onClick={toggleShowLogs} aria-pressed={showLogs}>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[12.5px] font-medium tracking-[-0.005em] text-ink">
                  Show full logs
                </span>
                <span className="text-[11.5px] text-ink-3">
                  {showLogs
                    ? 'Chat reveals every tool call and result.'
                    : 'Chat hides intermediate tool steps.'}
                </span>
              </span>
              <Switch on={showLogs} />
            </button>
          </div>

          <div className={SECTION_CLASS}>
            <div className={SECTION_LABEL_CLASS}>Tools</div>
            <div className="flex flex-col gap-1.5 px-1 [&>a]:w-full [&>a]:justify-start [&>button]:w-full [&>button]:justify-start">
              <Link href="/agents" className={buttonClassName()} onClick={() => setOpen(false)}>
                Agents →
              </Link>
              <Link
                href={sponsorId ? `/computer-use?employer_id=${sponsorId}` : '/computer-use'}
                className={buttonClassName()}
                onClick={() => setOpen(false)}
              >
                Console →
              </Link>
              <Link href="/admin/memory" className={buttonClassName()} onClick={() => setOpen(false)}>
                Memory →
              </Link>
              <Link href="/admin/mock-data" className={buttonClassName()} onClick={() => setOpen(false)}>
                Mock Sponsor Data Generator →
              </Link>
            </div>
          </div>

          <DemoSection />

          <div className={SECTION_CLASS}>
            <div className={SECTION_LABEL_CLASS}>Reports</div>
            <div className="flex flex-col gap-1.5 px-1 [&>a]:w-full [&>a]:justify-start [&>button]:w-full [&>button]:justify-start">
              <ActivityLogButton />
              <Link href="/event-log" className={buttonClassName()} onClick={() => setOpen(false)}>
                Event log →
              </Link>
              <Link href="/analytics" className={buttonClassName()} onClick={() => setOpen(false)}>
                Usage analytics →
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className={`relative inline-flex h-[30px] cursor-pointer items-center gap-1.5 rounded-full border px-3 font-sans text-xs font-medium tracking-[-0.005em] shadow-2 transition-[background-color,color,border-color,transform] duration-100 active:translate-y-px ${
          open
            ? 'border-ink bg-ink text-white [&_svg]:text-white'
            : 'border-line-2 bg-bg-2 text-ink-2 [&_svg]:text-ink-3 hover:bg-bg-hover hover:text-ink'
        }`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label="Admin controls"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
        <span>Admin</span>
        {(autoConfirm || showLogs) && !open ? (
          <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-acc" aria-hidden="true" />
        ) : null}
      </button>
    </div>
  );
};

const SECTION_CLASS = 'border-b border-line px-3 pb-3 pt-2.5 last:border-b-0';
const SECTION_LABEL_CLASS =
  'pb-1.5 pl-1 pr-1 text-[10.5px] font-medium uppercase tracking-[0.06em] text-ink-4';
const ROW_CLASS =
  'flex w-full cursor-pointer items-center justify-between gap-2.5 rounded-sm border border-transparent bg-transparent p-2 text-left transition-[background-color,border-color] duration-100 enabled:hover:bg-bg-hover disabled:cursor-not-allowed disabled:opacity-65';

const Switch: FunctionComponent<{ on: boolean }> = ({ on }) => (
  <span
    className={`relative h-4 w-7 shrink-0 rounded-full transition-colors duration-150 ${
      on ? 'bg-acc' : 'bg-line-2'
    }`}
    aria-hidden="true"
  >
    <span
      className={`absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white shadow-[0_1px_2px_rgba(15,17,21,0.18)] transition-transform duration-150 ${
        on ? 'translate-x-3' : ''
      }`}
    />
  </span>
);

interface Sponsor {
  id: string;
  name: string;
}

interface ResetPreview {
  thread_count: number;
  message_count: number;
  sender_email: string | null;
}

type DemoStage = 'idle' | 'confirm' | 'resetting';

const DemoSection: FunctionComponent = () => {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [employerId, setEmployerId] = useState<string>('');
  const [preview, setPreview] = useState<ResetPreview | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const [stage, setStage] = useState<DemoStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const [successLine, setSuccessLine] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    callApi(listSponsorsRoute, {
      gracefulFallback: () => {
        if (!cancelled) setSponsors([]);
      },
    }).then(data => {
      if (cancelled) return;
      const real = data.sponsors.filter(s => !s.id.startsWith('mock-'));
      setSponsors(real);
      if (real[0] && !employerId) setEmployerId(real[0].id);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!employerId) {
      setPreview(null);
      setPreviewError(false);
      return;
    }
    let cancelled = false;
    setPreviewError(false);
    callApi(previewResetDemoInboxRoute, {
      query: { employer_id: employerId },
      gracefulFallback: () => {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(true);
        }
      },
    }).then(data => {
      if (!cancelled) setPreview(data);
    });
    return () => {
      cancelled = true;
    };
  }, [employerId]);

  const selectedSponsor = sponsors.find(s => s.id === employerId) ?? null;
  const senderEmail = preview?.sender_email ?? null;
  const senderMissing = preview !== null && senderEmail === null;
  const threadCount = preview?.thread_count ?? 0;
  const messageCount = preview?.message_count ?? 0;

  async function refreshPreview(): Promise<ResetPreview | null> {
    if (!employerId) return null;
    let fresh: ResetPreview | null = null;
    await callApi(previewResetDemoInboxRoute, {
      query: { employer_id: employerId },
      gracefulFallback: () => {
        setPreviewError(true);
      },
    }).then(data => {
      fresh = data;
      setPreview(data);
    });
    return fresh;
  }

  async function onReset(): Promise<void> {
    setStage('confirm');
    setError(null);
    setSuccessLine(null);
    await refreshPreview();
  }

  async function onConfirm(): Promise<void> {
    if (!employerId || !selectedSponsor) return;
    setStage('resetting');
    setError(null);
    const result = await callApi(resetDemoInboxRoute, {
      body: { employer_id: employerId },
      gracefulFallback: () => {
        setError('Something went wrong');
        setStage('idle');
        showToast('Reset failed', { level: 'error' });
      },
    });
    setStage('idle');
    const line = `Reset ${selectedSponsor.name} — deleted ${result.threads_deleted} thread${result.threads_deleted === 1 ? '' : 's'}, sent ${result.sent_subjects.length} email${result.sent_subjects.length === 1 ? '' : 's'}.`;
    setSuccessLine(line);
    showToast(line, { level: 'success' });
    setTimeout(() => setSuccessLine(s => (s === line ? null : s)), 3000);
    void refreshPreview();
  }

  function onCancel(): void {
    setStage('idle');
    setError(null);
  }

  return (
    <div className={SECTION_CLASS}>
      <div className={SECTION_LABEL_CLASS}>Demo</div>
      <div className="flex flex-col gap-2 px-1">
        <label className="flex flex-col gap-1 text-[11.5px] text-ink-3">
          <span className="font-medium uppercase tracking-wider text-ink-4 text-[10.5px]">Sponsor</span>
          <select
            value={employerId}
            onChange={e => {
              setEmployerId(e.target.value);
              setStage('idle');
              setError(null);
              setSuccessLine(null);
            }}
            disabled={stage === 'resetting'}
            className="h-7 px-2 rounded-sm border border-line-2 bg-bg-2 font-sans text-[12.5px] text-ink outline-none focus:border-acc"
          >
            {sponsors.length === 0 ? <option value="">No clients available</option> : null}
            {sponsors.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        {stage === 'confirm' && selectedSponsor && preview ? (
          <div className="flex flex-col gap-2 rounded-sm border border-line bg-bg-hover p-2.5">
            <div className="text-[12px] font-semibold text-ink">
              Reset demo inbox for {selectedSponsor.name}?
            </div>
            <div className="text-[11.5px] leading-[1.55] text-ink-3">
              This will permanently delete every email thread, message, and attachment we have on file for{' '}
              {selectedSponsor.name} ({threadCount} thread{threadCount === 1 ? '' : 's'}, {messageCount}{' '}
              message{messageCount === 1 ? '' : 's'}), plus any pending Matilda drafts attached to them.
            </div>
            <div className="text-[11.5px] leading-[1.55] text-ink-3">
              Then it will send 4 fresh demo emails from{' '}
              <span className="font-mono text-ink-2">{senderEmail}</span> into the inbox:
              <ul className="ml-3 mt-0.5 list-disc text-ink-3">
                <li>December payroll (with attachment)</li>
                <li>Question about Sarah J</li>
                <li>Out of office</li>
                <li>Re: Year-end data request</li>
              </ul>
            </div>
            <div className="text-[11px] leading-[1.5] text-ink-4">
              The 4 emails route through AgentMail and may take a few seconds to appear after this returns.
              Files already uploaded to storage and messages on the AgentMail side are not removed — only
              our DB. This action is not reversible.
            </div>
            <div className="mt-1 flex justify-end gap-1.5">
              <Button type="button" size="small" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="button" size="small" variant="primary" onClick={onConfirm}>
                Confirm
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            size="small"
            onClick={onReset}
            disabled={
              !employerId || senderMissing || previewError || stage === 'resetting' || preview === null
            }
          >
            {stage === 'resetting' ? 'Resetting…' : 'Reset demo inbox'}
          </Button>
        )}

        {senderMissing ? (
          <div className="text-[11px] text-ink-4">Add an email contact to this client first.</div>
        ) : null}
        {error ? <div className="text-[11.5px] text-rose">{error}</div> : null}
        {successLine ? <div className="text-[11.5px] text-green">{successLine}</div> : null}
      </div>
    </div>
  );
};
